/* =============================================================================
   DiSEGNO Builder — Centralized Analytics layer
   -----------------------------------------------------------------------------
   ONE service. Every part of the builder calls `Disegno.Analytics.track(name,
   props)` and nothing else. No vendor SDK is referenced anywhere in the builder
   UI code.

   Pipeline:
     track(name, props)
       -> buildEvent()          standardize the envelope (ids, ts, design meta)
       -> standardEcommerceGuard drop names Shopify already tracks natively
       -> for each destination in forwarding[name]:
            Consent.allows(dest) ?      per-destination consent gate
            adapter enabled & present ? per-destination on/off
            scrubForDestination()       strip PII the destination disallows
            adapter.send(event)         inside try/catch — never throws upward

   A failing or missing destination NEVER breaks the builder or the other
   destinations. Debug mode logs the full envelope + per-destination outcome and
   suppresses real network sends.

   Configure once from the theme (see disegno-builder.liquid):
     Disegno.Analytics.configure(window.DisegnoConfig.analytics)
   ========================================================================== */
(function (root) {
  'use strict';

  var Disegno = (root.Disegno = root.Disegno || {});
  if (Disegno.Analytics) return;

  var Identity = Disegno.Identity;
  var Consent = Disegno.Consent;

  /* --------------------------------------------------------------------------
     DEFAULT FORWARDING MAP
     Which internal events go to which destinations out of the box. The theme
     can override any row via config.forwarding. `klaviyo` deliberately gets a
     SMALL, low-frequency subset (see spec §18). High-frequency UI events
     (element_reordered, builder_zoom_used) go to product-analytics only.
     -------------------------------------------------------------------------- */
  var DEFAULT_FORWARDING = {
    builder_viewed:          ['shopify', 'ga4'],
    builder_started:         ['shopify', 'ga4', 'meta', 'klaviyo'],
    jewelry_type_selected:   ['shopify', 'ga4', 'klaviyo'],
    element_added:           ['shopify', 'ga4'],           // + klaviyo only if config.klaviyo.highVolume
    element_removed:         ['shopify', 'ga4'],
    element_replaced:        ['shopify', 'ga4'],           // remove+add in one gesture — ONE event, not two
    element_reordered:       ['shopify'],                  // product analytics only — noisy
    trash_drop_used:         ['shopify'],                  // UX: element removed by dragging to the trash zone
    curated_design_selected: ['shopify', 'ga4', 'meta', 'klaviyo'],
    curated_design_modified: ['shopify', 'ga4', 'klaviyo'], // fires ONCE per curated base — keeps the original curated_design_id
    design_remixed:          ['shopify', 'ga4', 'klaviyo'],
    design_saved:            ['shopify', 'ga4', 'meta'],   // Klaviyo gets it direct from SavedDesign (Client API, keyed on the typed email)
    design_loaded:           ['shopify', 'ga4'],
    design_added_to_cart:    ['shopify', 'ga4', 'meta', 'klaviyo'], // CUSTOM event, not `add_to_cart`
    builder_zoom_used:       ['shopify'],                  // UX only

    /* Jewelry-type switch inside the builder (design preserved / adapted / reset). */
    jewelry_type_changed:    ['shopify', 'ga4', 'klaviyo'],

    /* Undo / Redo — SEPARATE UX events. They do NOT re-emit element_added /
       element_removed; the true design history lives in the builder's snapshot
       stack, so product-add/remove analytics are never inflated. Product
       analytics only. */
    builder_undo:            ['shopify'],
    builder_redo:            ['shopify'],

    /* ---- Personalization quiz ("Find your DiSEGNO") ----
       High-frequency step events (question_viewed / answer_selected /
       question_back) go to product analytics only. Funnel + outcome events
       carry attribution (quiz_session_id -> builder_session_id -> design_id). */
    quiz_viewed:                      ['shopify', 'ga4'],
    quiz_started:                     ['shopify', 'ga4', 'meta', 'klaviyo'],
    quiz_question_viewed:             ['shopify'],
    quiz_answer_selected:             ['shopify'],
    quiz_question_back:               ['shopify'],
    quiz_abandoned:                   ['shopify', 'ga4'],
    quiz_completed:                   ['shopify', 'ga4', 'meta', 'klaviyo'],
    quiz_recommendation_generated:    ['shopify', 'ga4', 'klaviyo'],
    quiz_recommendation_remixed:      ['shopify', 'ga4', 'klaviyo'],
    quiz_recommendation_added_to_cart:['shopify', 'ga4', 'meta', 'klaviyo'],
    quiz_recommendation_purchased:    ['shopify', 'ga4', 'meta', 'klaviyo'],

    /* ---- Starter-product check before cart (every path: quiz, manual build,
       curated, modified curated, remix, recovered saved design) ---- */
    starter_offer_shown:              ['shopify', 'ga4'],
    starter_already_included:         ['shopify'],
    starter_added:                    ['shopify', 'ga4', 'klaviyo'],
    starter_declined:                 ['shopify', 'ga4'],
    /* earrings: how many starter studs the customer is buying (0 | 1 | 2), and
       whether they already own their starter earrings — used to size the
       returning-customer / replenishment segment. */
    starter_quantity_selected:        ['shopify', 'ga4', 'klaviyo'],
    starter_already_owned:            ['shopify', 'ga4', 'klaviyo'],

    /* ---- Birthday reminders (optional, consent-based) ----
       No birth YEAR is ever collected or sent. `birthday_added` forwards only
       non-identifying fields (month, day, relationship label, consent flag,
       source) plus the record id; recipient name stays out of ad/product
       analytics and is only ever persisted server-side (App Proxy) / Klaviyo
       when consent is satisfied. */
    birthday_reminder_offered:        ['shopify'],
    birthday_reminder_accepted:       ['shopify', 'ga4', 'klaviyo'],
    birthday_reminder_declined:       ['shopify'],
    birthday_added:                   ['shopify', 'klaviyo']
  };

  /* Names Shopify's own analytics / Customer Events tracks natively. We never
     re-emit these; our custom events carry design metadata so the native
     ecommerce events can be joined to a design downstream (via cart attributes
     + order attributes — see disegno.cart.js). */
  var NATIVE_ECOMMERCE = {
    page_view: 1, page_viewed: 1,
    view_item: 1, product_viewed: 1,
    add_to_cart: 1, product_added_to_cart: 1,
    begin_checkout: 1, checkout_started: 1,
    add_payment_info: 1, add_shipping_info: 1,
    purchase: 1, checkout_completed: 1,
    search: 1, view_item_list: 1, select_item: 1
  };

  var config = {
    debug: false,
    context: {},               // static props merged into every event (page_type, template, locale...)
    forwarding: {},             // overrides merged over DEFAULT_FORWARDING
    destinations: {
      debug:   { enabled: true },
      shopify: { enabled: false },  // Shopify Customer Events bridge (see customer-events pixel)
      ga4:     { enabled: false, measurementId: null },
      meta:    { enabled: false, pixelId: null },
      klaviyo: { enabled: false, companyId: null, highVolume: false }
    }
  };

  var adapters = {};       // name -> adapter object
  var initialized = {};    // name -> bool
  var queue = [];          // events blocked only by "consent not yet decided"
  var log = [];            // debug ring buffer
  var LOG_MAX = 500;
  var forwardingResolved = merge({}, DEFAULT_FORWARDING);

  /* ---- config ---------------------------------------------------------- */
  function configure(opts) {
    opts = opts || {};
    if (typeof opts.debug === 'boolean') config.debug = opts.debug;
    if (opts.context) config.context = merge(config.context, opts.context);
    if (opts.forwarding) config.forwarding = merge(config.forwarding, opts.forwarding);
    if (opts.destinations) {
      for (var d in opts.destinations) {
        if (!Object.prototype.hasOwnProperty.call(opts.destinations, d)) continue;
        config.destinations[d] = merge(config.destinations[d] || {}, opts.destinations[d]);
      }
    }
    if (opts.consent && Consent && Consent.configure) Consent.configure(opts.consent);
    forwardingResolved = merge(merge({}, DEFAULT_FORWARDING), config.forwarding);
    initAdapters();
    // klaviyo opt-in for element_added only when explicitly allowed
    if (config.destinations.klaviyo && config.destinations.klaviyo.highVolume) {
      if (forwardingResolved.element_added.indexOf('klaviyo') === -1) {
        forwardingResolved.element_added = forwardingResolved.element_added.concat(['klaviyo']);
      }
    }
    if (config.debug) dbg('configured', { config: clone(config), forwarding: forwardingResolved });
    return Disegno.Analytics;
  }

  function registerAdapter(name, adapter) {
    adapters[name] = adapter || {};
    initialized[name] = false;
    initAdapters();
    return Disegno.Analytics;
  }

  function initAdapters() {
    for (var name in adapters) {
      if (!Object.prototype.hasOwnProperty.call(adapters, name)) continue;
      var dcfg = config.destinations[name] || {};
      var a = adapters[name];
      if (name !== 'debug' && !dcfg.enabled) continue;
      if (initialized[name]) continue;
      try {
        if (typeof a.init === 'function') a.init(dcfg, { debug: config.debug });
        initialized[name] = true;
      } catch (e) {
        dbg('adapter_init_failed', { adapter: name, error: String(e) });
      }
    }
  }

  /* ---- event envelope ------------------------------------------------------
     Standard properties on EVERY event (spec §14). Element / curated / earring
     specifics are passed by the caller in `props` and merged on top.
     -------------------------------------------------------------------------- */
  function buildEvent(name, props) {
    props = props || {};
    var meta = (Identity && Identity.getDesignMeta && Identity.getDesignMeta()) || {};
    var link = (Identity && Identity.getLink && Identity.getLink()) || {};
    var env = {
      event_name: name,
      event_id: (Identity && Identity.uuid ? Identity.uuid() : String(Date.now()) + Math.random()),
      timestamp: new Date().toISOString(),
      builder_session_id: (Identity && Identity.getSessionId && Identity.getSessionId()) || null,
      design_id: (Identity && Identity.getDesignId && Identity.getDesignId()) || null,
      // Quiz attribution — present on every event once a quiz has been started
      // this session, so quiz -> builder -> cart -> order joins on one id.
      quiz_session_id: props.quiz_session_id ||
        (Identity && Identity.getQuizSessionId && Identity.getQuizSessionId()) || null,
      identified: !!link.identified,
      // design-level context (falls back to identity meta cache)
      jewelry_type: props.jewelry_type || meta.jewelry_type || null,
      design_source: props.design_source || meta.design_source || null,
      element_count: pick(props.element_count, meta.element_count, null),
      design_total: pick(props.design_total, meta.design_total, null),
      currency: props.currency || meta.currency || (config.context && config.context.currency) || null
    };
    // static page/theme context
    for (var c in config.context) if (has(config.context, c) && env[c] == null) env[c] = config.context[c];
    // caller props win
    for (var k in props) if (has(props, k)) env[k] = props[k];
    return env;
  }

  /* ---- track --------------------------------------------------------------- */
  function track(name, props) {
    if (!name) return;
    if (NATIVE_ECOMMERCE[name]) {
      dbg('dropped_native_ecommerce', { event_name: name });
      return;
    }
    var event = buildEvent(name, props);
    var dests = forwardingResolved[name] || ['shopify']; // default: product analytics only
    var outcome = { event: event, sent: [], skipped: [] };

    for (var i = 0; i < dests.length; i++) {
      var dest = dests[i];
      var dcfg = config.destinations[dest] || {};
      var reason = null;

      if (dest !== 'debug' && !dcfg.enabled) reason = 'destination_disabled';
      else if (!adapters[dest]) reason = 'adapter_missing';
      else if (Consent && Consent.allows && !Consent.allows(dest)) reason = 'consent_denied';
      else if (adapters[dest].supports && !adapters[dest].supports(name)) reason = 'unsupported_by_adapter';

      if (reason) {
        outcome.skipped.push({ dest: dest, reason: reason });
        continue;
      }
      var payload = scrubForDestination(event, dest);
      try {
        adapters[dest].send(payload, { destination: dest, config: dcfg });
        outcome.sent.push(dest);
      } catch (e) {
        outcome.skipped.push({ dest: dest, reason: 'adapter_threw:' + String(e) });
      }
    }

    pushLog(outcome);
    if (config.debug) dbg('track', { event_name: name, sent: outcome.sent, skipped: outcome.skipped, event: event });
    return outcome;
  }

  /* ---- identify ---------------------------------------------------------------
     Called by the builder ONLY after the customer voluntarily becomes
     identifiable. `traits` MAY include `email` — it is forwarded ONLY to
     destinations that permit it (klaviyo, shopify server context) and is
     stripped for ga4 / meta / anything URL-bound.
     -------------------------------------------------------------------------- */
  function identify(traits) {
    traits = traits || {};
    var link = (Identity && Identity.link) ? Identity.link({
      shopify_customer_id: traits.shopify_customer_id,
      klaviyo_exchange_id: traits.klaviyo_exchange_id,
      email_provided: !!traits.email,
      source: traits.source,
      design_id: traits.design_id
    }) : { identified: true };

    var base = {
      builder_session_id: Identity && Identity.getSessionId ? Identity.getSessionId() : null,
      design_id: traits.design_id || (Identity && Identity.getDesignId ? Identity.getDesignId() : null),
      design_ids: link.design_ids || null,
      shopify_customer_id: traits.shopify_customer_id || null,
      source: traits.source || null
    };

    var results = [];
    var dests = ['klaviyo', 'ga4', 'shopify']; // meta intentionally excluded from identify
    for (var i = 0; i < dests.length; i++) {
      var dest = dests[i];
      var dcfg = config.destinations[dest] || {};
      if (dest !== 'debug' && !dcfg.enabled) { results.push({ dest: dest, reason: 'disabled' }); continue; }
      if (!adapters[dest] || typeof adapters[dest].identify !== 'function') { results.push({ dest: dest, reason: 'no_identify' }); continue; }
      if (Consent && Consent.allows && !Consent.allows(dest)) { results.push({ dest: dest, reason: 'consent_denied' }); continue; }
      var t = scrubIdentifyForDestination(merge(clone(base), traits), dest);
      try {
        adapters[dest].identify(t, { destination: dest, config: dcfg });
        results.push({ dest: dest, ok: true });
      } catch (e) {
        results.push({ dest: dest, reason: 'threw:' + String(e) });
      }
    }
    if (config.debug) dbg('identify', { results: results, base: base });
    return results;
  }

  /* ---- PII scrubbing ---------------------------------------------------------
     GA4 & Meta must never receive email / phone / name / full address, and
     nothing PII may end up in a URL. Klaviyo may receive email/phone (that's
     its purpose). Shopify Customer Events runs server-side after checkout so it
     may retain customer id.
     -------------------------------------------------------------------------- */
  var PII_KEYS = ['email', 'phone', 'first_name', 'last_name', 'name', 'address', 'address1', 'address2', 'zip', 'postal_code'];

  function scrubForDestination(event, dest) {
    if (dest === 'klaviyo' || dest === 'shopify' || dest === 'debug') return event;
    var out = clone(event);
    for (var i = 0; i < PII_KEYS.length; i++) delete out[PII_KEYS[i]];
    // also strip any nested `customer` object for ad pixels
    if (dest === 'meta' || dest === 'ga4') delete out.customer;
    return out;
  }
  function scrubIdentifyForDestination(traits, dest) {
    var out = clone(traits);
    if (dest === 'ga4') {
      for (var i = 0; i < PII_KEYS.length; i++) delete out[PII_KEYS[i]];
    }
    return out;
  }

  /* ---- debug / inspection ------------------------------------------------- */
  function setDebug(on) { config.debug = !!on; return Disegno.Analytics; }
  function getLog() { return log.slice(); }
  function clearLog() { log.length = 0; }
  function getConfig() { return clone(config); }
  function getForwarding() { return clone(forwardingResolved); }

  function pushLog(entry) {
    log.push({ at: Date.now(), event_name: entry.event.event_name, sent: entry.sent, skipped: entry.skipped, event: entry.event });
    if (log.length > LOG_MAX) log.splice(0, log.length - LOG_MAX);
    try {
      root.dispatchEvent(new CustomEvent('disegno:analytics', { detail: entry }));
    } catch (e) {}
  }
  function dbg(kind, data) {
    if (!config.debug) return;
    try {
      // eslint-disable-next-line no-console
      console.groupCollapsed('%c[DiSEGNO analytics] ' + kind, 'color:#c33820;font-weight:600');
      // eslint-disable-next-line no-console
      console.log(data);
      // eslint-disable-next-line no-console
      console.groupEnd();
    } catch (e) {}
  }

  /* ---- tiny utils ---------------------------------------------------------- */
  function has(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
  function pick() { for (var i = 0; i < arguments.length; i++) if (arguments[i] != null) return arguments[i]; return null; }
  function clone(o) { try { return JSON.parse(JSON.stringify(o)); } catch (e) { return o; } }
  function merge(a, b) {
    a = a || {};
    if (!b) return a;
    for (var k in b) {
      if (!has(b, k)) continue;
      if (b[k] && typeof b[k] === 'object' && !Array.isArray(b[k]) && a[k] && typeof a[k] === 'object') a[k] = merge(a[k], b[k]);
      else a[k] = b[k];
    }
    return a;
  }

  Disegno.Analytics = {
    configure: configure,
    registerAdapter: registerAdapter,
    track: track,
    identify: identify,
    buildEvent: buildEvent,
    setDebug: setDebug,
    getLog: getLog,
    clearLog: clearLog,
    getConfig: getConfig,
    getForwarding: getForwarding,
    DEFAULT_FORWARDING: DEFAULT_FORWARDING,
    NATIVE_ECOMMERCE: NATIVE_ECOMMERCE
  };

  // Retry consent-blocked events when consent is later collected.
  if (Consent && Consent.onChange) {
    Consent.onChange(function () {
      var pending = queue.splice(0, queue.length);
      for (var i = 0; i < pending.length; i++) track(pending[i].name, pending[i].props);
    });
  }
})(typeof window !== 'undefined' ? window : this);
